import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  CircularProgress,
  Slider,
  Chip,
  Grid,
  Divider,
  FormHelperText,
  Skeleton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import promptService, { Prompt, HarmonySettings } from '../../services/promptService';

const SymphonySettingsComponent: React.FC = () => {
  console.log('SymphonySettingsComponent rendering');

  const [settings, setSettings] = useState<HarmonySettings>({
    prompt_id: 'default',
    chunk_size: 2000,
    chunk_overlap: 200,
    extraction_model: 'claude-3-5-sonnet-20241022',
    document_type: 'general',
  });

  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const models = promptService.getAvailableModels();
  const documentTypes = promptService.getDocumentTypes();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    console.log('loadSettings called');
    setLoading(true);
    setError(null);

    try {
      // Load both prompts and settings in parallel
      console.log('Fetching symphony prompts and settings...');
      const [fetchedPrompts, fetchedSettings] = await Promise.all([
        promptService.getSymphonyPrompts(),
        promptService.getSymphonySettings(),
      ]);

      console.log('Fetched prompts:', fetchedPrompts);
      console.log('Fetched settings:', fetchedSettings);

      setPrompts(fetchedPrompts);
      setSettings(fetchedSettings);
    } catch (err: any) {
      console.error('Failed to load settings:', err);
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Only send the fields we want to update, not the ID or timestamps
      const settingsToSave = {
        prompt_id: settings.prompt_id,
        chunk_size: settings.chunk_size,
        chunk_overlap: settings.chunk_overlap,
        extraction_model: settings.extraction_model,
        document_type: settings.document_type,
      };

      const updatedSettings = await promptService.updateSymphonySettings(settingsToSave);
      // Update local state with the returned settings (including new ID and timestamps)
      setSettings(updatedSettings);
      setSuccess('Settings saved successfully');
    } catch (err: any) {
      console.error('Failed to save settings:', err);
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    loadSettings();
    setSuccess(null);
    setError(null);
  };

  console.log('Current state - loading:', loading, 'error:', error);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" height={40} width={300} />
        <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
        <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
        <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* AI Processing Configuration */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              AI Processing Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure how documents are processed by the AI system
            </Typography>

            {/* Prompt Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Extraction Prompt</InputLabel>
              <Select
                value={settings.prompt_id}
                onChange={(e) => setSettings({ ...settings, prompt_id: e.target.value })}
                label="Extraction Prompt"
              >
                <MenuItem value="default">
                  <em>Default Prompt</em>
                </MenuItem>
                {prompts.map((prompt) => (
                  <MenuItem key={prompt.id} value={prompt.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{prompt.prompt_name}</span>
                      {prompt.is_active && (
                        <Chip label="Active" size="small" color="success" />
                      )}
                      <Chip label={`v${prompt.version}`} size="small" variant="outlined" />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Select the prompt template for entity and relationship extraction
              </FormHelperText>
            </FormControl>

            {/* Model Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>AI Model</InputLabel>
              <Select
                value={settings.extraction_model}
                onChange={(e) => setSettings({ ...settings, extraction_model: e.target.value })}
                label="AI Model"
              >
                {models.map((model) => (
                  <MenuItem key={model} value={model}>
                    {model}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Choose the AI model for processing documents
              </FormHelperText>
            </FormControl>

            {/* Document Type */}
            <FormControl fullWidth>
              <InputLabel>Document Type</InputLabel>
              <Select
                value={settings.document_type}
                onChange={(e) => setSettings({ ...settings, document_type: e.target.value })}
                label="Document Type"
              >
                {documentTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Default document type for categorization
              </FormHelperText>
            </FormControl>
          </Paper>
        </Grid>

        {/* Chunking Configuration */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Text Chunking Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Control how documents are split into chunks for processing
            </Typography>

            {/* Chunk Size */}
            <Box sx={{ mb: 4 }}>
              <Typography gutterBottom>
                Chunk Size: {settings.chunk_size} characters
              </Typography>
              <Slider
                value={settings.chunk_size}
                onChange={(_, value) => setSettings({ ...settings, chunk_size: value as number })}
                min={500}
                max={5000}
                step={100}
                marks={[
                  { value: 500, label: '500' },
                  { value: 2000, label: '2000' },
                  { value: 5000, label: '5000' },
                ]}
                valueLabelDisplay="auto"
              />
              <FormHelperText>
                Size of each text chunk for processing (recommended: 1500-2500)
              </FormHelperText>
            </Box>

            {/* Chunk Overlap */}
            <Box sx={{ mb: 3 }}>
              <Typography gutterBottom>
                Chunk Overlap: {settings.chunk_overlap} characters
              </Typography>
              <Slider
                value={settings.chunk_overlap}
                onChange={(_, value) => setSettings({ ...settings, chunk_overlap: value as number })}
                min={0}
                max={500}
                step={50}
                marks={[
                  { value: 0, label: '0' },
                  { value: 200, label: '200' },
                  { value: 500, label: '500' },
                ]}
                valueLabelDisplay="auto"
              />
              <FormHelperText>
                Overlap between consecutive chunks to maintain context (recommended: 100-300)
              </FormHelperText>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Chunking Preview */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Chunking Preview
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" color="text.secondary">
                  With current settings, a 10,000 character document will be split into approximately{' '}
                  <strong>
                    {Math.ceil(10000 / (settings.chunk_size - settings.chunk_overlap))}
                  </strong>{' '}
                  chunks for processing.
                </Typography>
              </Paper>
            </Box>
          </Paper>
        </Grid>

        {/* Prompt Details (if a custom prompt is selected) */}
        {settings.prompt_id !== 'default' && prompts.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Selected Prompt Details
              </Typography>
              {(() => {
                const selectedPrompt = prompts.find(p => p.id === settings.prompt_id);
                if (!selectedPrompt) return null;

                return (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      {selectedPrompt.prompt_name}
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mt: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          maxHeight: 300,
                          overflow: 'auto',
                        }}
                      >
                        {selectedPrompt.prompt_text}
                      </Typography>
                    </Paper>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                      <Chip label={`Version ${selectedPrompt.version}`} size="small" />
                      <Chip label={`Created by ${selectedPrompt.created_by}`} size="small" />
                      <Chip
                        label={`Search: ${selectedPrompt.search_method}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                );
              })()}
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleReset}
          disabled={saving}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </Box>
  );
};

export default SymphonySettingsComponent;
export { SymphonySettingsComponent as SymphonySettings };
